import { useState } from 'react';
import { api } from '../../utils/api';

interface PortGeneratorProps {
  equipmentId: string;
  equipmentType?: string;
  onPortsGenerated: () => void;
}

export function PortGenerator({ equipmentId, equipmentType, onPortsGenerated }: PortGeneratorProps) {
  const allowedTypes = ['SERVER', 'SWITCH', 'ROUTER', 'PATCH_PANEL', 'FIREWALL', 'LOAD_BALANCER', 'KVM', 'CONSOLE_SERVER'];
  if (equipmentType && !allowedTypes.includes(equipmentType)) return null;

  const [prefix, setPrefix] = useState('eth');
  const [startIdx, setStartIdx] = useState(0);
  const [count, setCount] = useState(4);
  const [portType, setPortType] = useState('RJ45');
  const [loading, setLoading] = useState(false);

  const handleGenerate = async () => {
    setLoading(true);
    try {
      const portsToCreate = [];
      for (let i = 0; i < count; i++) {
        portsToCreate.push({
          name: `${prefix}${startIdx + i}`,
          port_type: portType,
          connector_type: portType
        });
      }
      
      await api.post(
        `/api/equipment/${equipmentId}/ports/batch`, 
        portsToCreate
      );
      onPortsGenerated();
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="p-4 rounded text-sm space-y-4 border"
      style={{
        backgroundColor: 'var(--bg-panel)',
        borderColor: 'var(--border-color)',
        color: 'var(--text-primary)',
      }}
    >
      <h3 className="font-semibold" style={{ color: 'var(--text-primary)' }}>No Ports Found</h3>
      <p style={{ color: 'var(--text-muted)' }}>Generate ports for this equipment to start patching.</p>
      
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="block mb-1 text-xs" style={{ color: 'var(--text-muted)' }}>Prefix</label>
          <input 
            type="text" 
            className="w-full rounded p-1 text-xs focus:outline-none transition-colors border"
            style={{
              backgroundColor: 'var(--bg-input)',
              borderColor: 'var(--border-color)',
              color: 'var(--text-primary)',
            }}
            value={prefix}
            onChange={e => setPrefix(e.target.value)}
          />
        </div>
        <div>
          <label className="block mb-1 text-xs" style={{ color: 'var(--text-muted)' }}>Start Index</label>
          <input 
            type="number" 
            className="w-full rounded p-1 text-xs focus:outline-none transition-colors border"
            style={{
              backgroundColor: 'var(--bg-input)',
              borderColor: 'var(--border-color)',
              color: 'var(--text-primary)',
            }}
            value={startIdx}
            onChange={e => setStartIdx(parseInt(e.target.value))}
          />
        </div>
        <div>
          <label className="block mb-1 text-xs" style={{ color: 'var(--text-muted)' }}>Count</label>
          <input 
            type="number" 
            className="w-full rounded p-1 text-xs focus:outline-none transition-colors border"
            style={{
              backgroundColor: 'var(--bg-input)',
              borderColor: 'var(--border-color)',
              color: 'var(--text-primary)',
            }}
            value={count}
            onChange={e => setCount(parseInt(e.target.value))}
          />
        </div>
        <div>
          <label className="block mb-1 text-xs" style={{ color: 'var(--text-muted)' }}>Type</label>
          <select 
            className="w-full rounded p-1 text-xs focus:outline-none transition-colors border"
            style={{
              backgroundColor: 'var(--bg-input)',
              borderColor: 'var(--border-color)',
              color: 'var(--text-primary)',
            }}
            value={portType}
            onChange={e => setPortType(e.target.value)}
          >
            <optgroup label="Data (Copper)">
              <option value="RJ45">RJ45 — 1G Copper</option>
              <option value="RJ45-10G">RJ45 — 10G Copper</option>
            </optgroup>
            <optgroup label="Data (Fiber / SFP)">
              <option value="SFP">SFP — 1G</option>
              <option value="SFP+">SFP+ — 10G</option>
              <option value="SFP28">SFP28 — 25G</option>
              <option value="QSFP+">QSFP+ — 40G</option>
              <option value="QSFP28">QSFP28 — 100G</option>
              <option value="QSFP56">QSFP56 — 200G</option>
              <option value="QSFP-DD">QSFP-DD — 400G</option>
              <option value="OSFP">OSFP — 400G</option>
            </optgroup>
            <optgroup label="DWDM / Optical">
              <option value="CFP2">CFP2 — 100G DWDM</option>
              <option value="CFP4">CFP4 — 100G DWDM</option>
              <option value="LC">LC Fiber (generic)</option>
            </optgroup>
            <optgroup label="Power">
              <option value="C13">C13 — 10A Power</option>
              <option value="C14">C14 — 10A Power (male)</option>
              <option value="C19">C19 — 16A Power</option>
              <option value="C20">C20 — 16A Power (male)</option>
            </optgroup>
            <optgroup label="Management / OOB">
              <option value="iDRAC">iDRAC (Dell management)</option>
              <option value="iLO">iLO (HPE management)</option>
              <option value="IPMI">IPMI (generic management)</option>
              <option value="RJ11">RJ11 — Console/Serial</option>
              <option value="USB-A">USB-A — Console OOB</option>
            </optgroup>
          </select>
        </div>
      </div>
      
      <button 
        onClick={handleGenerate}
        disabled={loading}
        className="w-full bg-accent hover:bg-accent-hover text-white font-medium py-1.5 rounded transition disabled:opacity-50"
      >
        {loading ? 'Generating...' : 'Generate Ports'}
      </button>
    </div>
  );
}
